import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Grid
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FileText, SquaresFour, X } from '@phosphor-icons/react';
import api from '../../../utils/api';

/** Subtle corners on picker tiles (px strings — numeric sx values are multiplied by theme.shape.borderRadius). */
const TILE_RADIUS_PX = '4px';

/** First image block URL in the proposal layout tree (for template thumbnails). */
function findFirstImageUrl(blocks) {
  if (!Array.isArray(blocks)) return null;
  for (const b of blocks) {
    if (!b || typeof b !== 'object') continue;
    if (b.type === 'image' && b.props?.url) return b.props.url;
    if (Array.isArray(b.children) && b.children.length) {
      const nested = findFirstImageUrl(b.children);
      if (nested) return nested;
    }
  }
  return null;
}

function CreateNewProposalDialog({ open, onClose, businessId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !businessId) return;
    setLoading(true);
    api
      .get(`/api/agreements/business/${businessId}/templates`)
      .then((res) => setTemplates(res.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [open, businessId]);

  const templatesWithPreview = useMemo(
    () =>
      templates.map((t) => ({
        ...t,
        previewUrl: findFirstImageUrl(t?.content?.blocks)
      })),
    [templates]
  );

  const handleClose = () => {
    onClose(undefined);
  };

  const handlePickTemplate = (templateId) => {
    onClose(templateId);
  };

  const handleStartWithoutTemplate = () => {
    onClose(null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          pb: 1
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
          Create new agreement
        </Typography>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <X size={22} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Choose a template to pre-fill your agreement, or start without one.
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              {/*
                Not using MUI Card here: global MuiCard theme overrides set a solid 1px border
                and hover rules that fight `sx`, so a dashed border never wins reliably.
              */}
              <Box
                role="button"
                tabIndex={0}
                onClick={handleStartWithoutTemplate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStartWithoutTemplate();
                  }
                }}
                sx={(theme) => ({
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: TILE_RADIUS_PX,
                  border: `2px dashed ${theme.palette.grey[400]}`,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, border-color 0.2s, background-color 0.2s',
                  '&:hover': {
                    border: `2px dashed ${theme.palette.primary.main}`,
                    boxShadow: theme.shadows[2],
                    backgroundColor: 'rgba(255, 255, 255, 0.65)'
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2
                  }
                })}
              >
                <Box
                  sx={{
                    aspectRatio: '4 / 3',
                    flex: '0 0 auto',
                    bgcolor: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 0,
                    width: '100%',
                    p: 1.5
                  }}
                >
                  <SquaresFour size={48} weight="duotone" style={{ opacity: 0.45 }} />
                </Box>
                <Box sx={{ px: 1.5, py: 1.5, textAlign: 'center', flexShrink: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Start a blank agreement
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {templatesWithPreview.map((t) => {
              const name = t.templateName || 'Untitled template';
              return (
                <Grid item xs={12} sm={6} md={4} key={t._id}>
                  {/*
                    One outer box owns border + radius + clip (same pattern as “blank” tile).
                    Avoid MUI Card + CardActionArea: a second rounded, overflow:hidden layer
                    makes rounding look like it belongs to the image only.
                  */}
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => handlePickTemplate(t._id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePickTemplate(t._id);
                      }
                    }}
                    sx={(theme) => ({
                      height: '100%',
                      boxSizing: 'border-box',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: TILE_RADIUS_PX,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(10px)',
                      border: `2px solid ${alpha(theme.palette.common.black, 0.14)}`,
                      transition: theme.transitions.create(
                        ['border-color', 'box-shadow', 'background-color'],
                        { duration: theme.transitions.duration.shorter }
                      ),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: theme.shadows[2],
                        backgroundColor: 'rgba(255, 255, 255, 0.65)'
                      },
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2
                      }
                    })}
                  >
                    <Box
                      sx={{
                        aspectRatio: '4 / 3',
                        bgcolor: 'action.hover',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 0,
                        width: '100%',
                        overflow: 'hidden'
                      }}
                    >
                      {t.previewUrl ? (
                        <Box
                          component="img"
                          src={t.previewUrl}
                          alt=""
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      ) : (
                        <FileText size={48} weight="duotone" style={{ opacity: 0.45 }} />
                      )}
                    </Box>
                    <Box sx={{ px: 1.5, py: 1.5, textAlign: 'center' }}>
                      <Typography variant="subtitle2" fontWeight={600} noWrap title={name}>
                        {name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CreateNewProposalDialog;
